import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { api } from "../lib/api";
import { PageHeader } from "../components/PageHeader";
import { StatusBadge } from "../components/StatusBadge";
import { Loading, ErrorState, EmptyState } from "../components/EmptyState";
import { useMe } from "../lib/hooks";
import { monthlyResetLabel } from "../lib/format";

function Stat({
  label,
  value,
  hint,
}: {
  label: string;
  value: React.ReactNode;
  hint?: string;
}) {
  return (
    <div className="card px-4 py-3.5">
      <div className="text-xs font-medium uppercase tracking-wide text-ink-subtle">
        {label}
      </div>
      <div className="mt-1 text-2xl font-semibold text-ink">{value}</div>
      {hint && <div className="mt-0.5 text-xs text-ink-muted">{hint}</div>}
    </div>
  );
}

export function OverviewPage() {
  const me = useMe();
  const connectors = useQuery({
    queryKey: ["connectors"],
    queryFn: () => api.connectors.list(),
  });
  const keys = useQuery({ queryKey: ["keys"], queryFn: () => api.keys.list() });
  const processed = useQuery({
    queryKey: ["stats", "processed"],
    queryFn: () => api.stats.processed(),
  });
  const usage = useQuery({ queryKey: ["usage"], queryFn: () => api.usage() });

  const activeKeys = keys.data?.filter((k) => k.status === "active").length ?? 0;
  const filesProcessed = processed.data?.files_processed ?? null;

  return (
    <div>
      <PageHeader
        title={`Welcome${me.data?.user.name ? `, ${me.data.user.name}` : ""}`}
        description="At-a-glance status for your BlobShip connectors, keys, and recent activity."
      />

      {usage.data && usage.data.limit != null && (
        <div className="card mb-6 px-4 py-3.5">
          <div className="flex items-center justify-between">
            <span className="text-xs font-medium uppercase tracking-wide text-ink-subtle">
              Free uploads this month
            </span>
            <span className="font-mono text-xs text-ink-muted">
              {usage.data.used} / {usage.data.limit}
            </span>
          </div>
          <p className="mt-1 text-xs text-ink-subtle">
            Resets {monthlyResetLabel()}.
          </p>
          <div className="mt-2 h-2 w-full overflow-hidden rounded-full bg-surface-100">
            <div
              className={`h-full rounded-full ${
                usage.data.used >= usage.data.limit ? "bg-warn" : "bg-accent"
              }`}
              style={{
                width: `${Math.min(100, (usage.data.used / usage.data.limit) * 100)}%`,
              }}
            />
          </div>
        </div>
      )}

      <div className="mb-6 grid grid-cols-1 gap-3 sm:grid-cols-3">
        <Stat
          label="Files processed"
          value={filesProcessed ?? "—"}
          hint="Across all connectors"
        />
        <Stat
          label="Active keys"
          value={activeKeys}
          hint={`${keys.data?.length ?? 0} total`}
        />
        <Stat
          label="Connectors"
          value={connectors.data?.length ?? 0}
          hint={`${
            connectors.data?.filter((c) => c.health === "ok").length ?? 0
          } healthy`}
        />
      </div>

      <div className="mb-2 flex items-center justify-between">
        <h2 className="text-sm font-semibold text-ink">Connector health</h2>
        <Link to="/connectors" className="text-sm font-medium text-accent">
          Manage →
        </Link>
      </div>

      {connectors.isLoading ? (
        <Loading />
      ) : connectors.isError ? (
        <ErrorState error={connectors.error} onRetry={connectors.refetch} />
      ) : connectors.data && connectors.data.length > 0 ? (
        <div className="card divide-y divide-surface-100">
          {connectors.data.map((c) => (
            <div
              key={c.id}
              className="flex items-center justify-between px-4 py-3"
            >
              <div>
                <div className="text-sm font-medium text-ink">
                  {c.displayName}
                </div>
                <div className="text-xs text-ink-subtle">
                  {c.cloud === "aws"
                    ? `AWS · ${c.awsRegion ?? "—"}`
                    : `Azure · ${c.azStorageAccount ?? "—"}`}
                </div>
              </div>
              <StatusBadge health={c.health} />
            </div>
          ))}
        </div>
      ) : (
        <EmptyState
          title="No connectors yet"
          description="Connect an AWS or Azure account to let BlobShip broker uploads into your own storage."
          action={
            <Link to="/connectors" className="btn-primary">
              Add a connector
            </Link>
          }
        />
      )}
    </div>
  );
}
